import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  ArrowLeft,
  Download,
  Package,
  Users,
  Euro,
  Truck,
  Calendar,
  Building2,
  FileSpreadsheet,
  Printer,
  RefreshCw,
  CheckCircle,
  Clock,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getErrorMessage } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface ProductSummary {
  product_id: string;
  product_name: string;
  total_quantity: number;
  total_value: number;
}

interface OrderSummary {
  order_id: string;
  company_name: string;
  delivery_address?: string;
  delivery_city?: string;
  delivery_postal_code?: string;
  delivery_province?: string;
  delivery_notes?: string;
  items: {
    product_id: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
  }[];
  total: number;
  contact_email?: string;
  contact_phone?: string;
}

interface GroupageSummary {
  groupage_id: string;
  groupage_title: string;
  association_name: string;
  seller_company_name: string;
  collection_start: string;
  collection_end: string;
  total_participants: number;
  total_orders: number;
  products_summary: ProductSummary[];
  grand_total: number;
  orders: OrderSummary[];
  generated_at: string;
}

const GroupageSummaryPage: React.FC = () => {
  const { groupageId } = useParams<{ groupageId: string }>();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<GroupageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (groupageId) {
      fetchSummary();
    }
  }, [groupageId]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/groupage/opportunities/${groupageId}/summary`);
      setSummary(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const res = await axios.get(`${API}/groupage/opportunities/${groupageId}/export?format=csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `groupage_${groupageId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Export completato');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Package className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Riepilogo non disponibile</h2>
        <p className="text-muted-foreground mb-4">
          Il Groupage deve essere chiuso per generare il riepilogo
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna indietro
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4" data-testid="groupage-summary-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Riepilogo Groupage</h1>
            <p className="text-muted-foreground">{summary.groupage_title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Stampa
          </Button>
          <Button onClick={handleExportCSV} disabled={exporting}>
            {exporting ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4 mr-2" />
            )}
            Esporta CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-bold">{summary.groupage_title}</h1>
        <p className="text-lg">{summary.association_name}</p>
        <p className="text-muted-foreground">
          Generato il {format(parseISO(summary.generated_at), "d MMMM yyyy 'alle' HH:mm", { locale: it })}
        </p>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-4 print:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Fornitore
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{summary.seller_company_name}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Partecipanti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total_participants}</div>
            <p className="text-xs text-muted-foreground">{summary.total_orders} ordini totali</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Periodo Raccolta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm">
              {format(parseISO(summary.collection_start), 'd MMM', { locale: it })} - 
              {format(parseISO(summary.collection_end), 'd MMM yyyy', { locale: it })}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Euro className="h-4 w-4" />
              Totale Generale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              € {summary.grand_total.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="print:hidden">
        <TabsList>
          <TabsTrigger value="products">
            <Package className="h-4 w-4 mr-2" />
            Riepilogo Prodotti
          </TabsTrigger>
          <TabsTrigger value="orders">
            <Truck className="h-4 w-4 mr-2" />
            Ordini per Spedizione
          </TabsTrigger>
        </TabsList>

        {/* Products Summary Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>Riepilogo Prodotti Aggregato</CardTitle>
              <CardDescription>
                Quantità totali ordinate per ciascun prodotto
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prodotto</TableHead>
                    <TableHead className="text-right">Quantità Totale</TableHead>
                    <TableHead className="text-right">Valore Totale</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.products_summary.map(product => (
                    <TableRow key={product.product_id}>
                      <TableCell className="font-medium">{product.product_name}</TableCell>
                      <TableCell className="text-right">{product.total_quantity}</TableCell>
                      <TableCell className="text-right">€ {product.total_value.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-bold">TOTALE</TableCell>
                    <TableCell className="text-right font-bold">
                      {summary.products_summary.reduce((acc, p) => acc + p.total_quantity, 0)}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      € {summary.grand_total.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Ordini per Spedizione Individuale</CardTitle>
                <CardDescription>
                  Lista completa degli ordini con indirizzi di consegna
                </CardDescription>
              </CardHeader>
            </Card>

            {summary.orders.map((order, index) => (
              <Card key={order.order_id} className="print:break-inside-avoid">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">
                        {index + 1}. {order.company_name}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" />
                        {order.delivery_address}, {order.delivery_postal_code} {order.delivery_city} ({order.delivery_province})
                      </CardDescription>
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      € {order.total.toFixed(2)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Prodotto</TableHead>
                        <TableHead className="text-right">Qtà</TableHead>
                        <TableHead className="text-right">Prezzo Unit.</TableHead>
                        <TableHead className="text-right">Totale</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.items.map(item => (
                        <TableRow key={item.product_id}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">€ {item.unit_price.toFixed(2)}</TableCell>
                          <TableCell className="text-right">€ {item.total_price.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {order.delivery_notes && (
                    <div className="mt-3 p-2 bg-muted rounded text-sm">
                      <strong>Note consegna:</strong> {order.delivery_notes}
                    </div>
                  )}
                  
                  {(order.contact_email || order.contact_phone) && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      Contatto: {order.contact_email} {order.contact_phone && `• ${order.contact_phone}`}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {summary.orders.length === 0 && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Package className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nessun ordine confermato</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Print version - both tabs visible */}
      <div className="hidden print:block space-y-6">
        <div>
          <h2 className="text-xl font-bold mb-4">Riepilogo Prodotti</h2>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Prodotto</th>
                <th className="text-right py-2">Quantità</th>
                <th className="text-right py-2">Valore</th>
              </tr>
            </thead>
            <tbody>
              {summary.products_summary.map(product => (
                <tr key={product.product_id} className="border-b">
                  <td className="py-2">{product.product_name}</td>
                  <td className="text-right py-2">{product.total_quantity}</td>
                  <td className="text-right py-2">€ {product.total_value.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td className="py-2">TOTALE</td>
                <td className="text-right py-2">
                  {summary.products_summary.reduce((acc, p) => acc + p.total_quantity, 0)}
                </td>
                <td className="text-right py-2">€ {summary.grand_total.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="page-break-before">
          <h2 className="text-xl font-bold mb-4">Ordini per Spedizione</h2>
          {summary.orders.map((order, index) => (
            <div key={order.order_id} className="mb-6 border-b pb-4">
              <h3 className="font-bold">{index + 1}. {order.company_name}</h3>
              <p className="text-sm mb-2">
                {order.delivery_address}, {order.delivery_postal_code} {order.delivery_city} ({order.delivery_province})
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Prodotto</th>
                    <th className="text-right py-1">Qtà</th>
                    <th className="text-right py-1">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map(item => (
                    <tr key={item.product_id}>
                      <td className="py-1">{item.product_name}</td>
                      <td className="text-right py-1">{item.quantity}</td>
                      <td className="text-right py-1">€ {item.total_price.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-right font-bold mt-1">Totale: € {order.total.toFixed(2)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GroupageSummaryPage;
